import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CsrStandard } from '../../../models/csrStandard';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { ColumnConfig, LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { LutService } from '../../../services/lut.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-csr-standard-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    NgxSpinnerModule
  ],
  templateUrl: './csr-standard-list.component.html',
  styleUrl: './csr-standard-list.component.scss'
})
export class CsrStandardListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  csrStandards = signal<CsrStandard[]>([]);

  csrStandardsColumns: ColumnConfig[] = [
    { key: 'name', header: 'Name', type: 'text', sortable: true, editable: true },
    { key: 'type', header: 'Type', type: 'select', sortable: true, editable: true, selectOptions: [
      { value: 1, label: 'Reporting' },
      { value: 2, label: 'Environmental' }
    ] }
  ];

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadCsrStandards();
  }

  loadCsrStandards(): void {
    this.spinnerService.show();
    this.lutService.getCsrStandards().subscribe({
      next: (data) => {
        const sortedData = this.sortCsrStandards(data);
        this.csrStandards.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading ESG standards:', error);
        this.spinnerService.hide();
      }
    });
  }

  private sortCsrStandards(standards: CsrStandard[]): CsrStandard[] {
    return [...standards].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }

  onCreateCsrStandard(): void {
    this.lookupTable.startAdd();
  }

  onItemSelected(csrStandard: CsrStandard): void {
  }

  onEditCsrStandard(csrStandard: CsrStandard): void {
  }

  onDeleteCsrStandard(csrStandard: CsrStandard): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(csrStandard.name)
      .subscribe(result => {
        if (result) {
          // Store original list for rollback in case of error
          const originalStandards = this.csrStandards();

          // 1. Optimistically remove from local state immediately
          this.csrStandards.update(standards => standards.filter(c => c.id !== csrStandard.id));

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteCsrStandard(csrStandard.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('ESG standard deleted successfully');
              // 3. On success: Already removed from UI, nothing more to do
            },
            error: (error) => {
              console.error('Error deleting ESG standard:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original list
              this.csrStandards.set(originalStandards);
              this.toast.error(`<strong>Failed to delete ESG Standard:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: CsrStandard, newName: string} | {item: CsrStandard, newValue: string, columnKey: string}): void {
    // Store original item for rollback in case of error
    const originalItem = { ...event.item };

    // Prepare updated item
    let updatedItem: CsrStandard;
    if ('newName' in event) {
      // Legacy format - simple name update
      updatedItem = { ...event.item, name: event.newName };
    } else {
      // New format - update specific column
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    // 1. Optimistically update local state immediately and re-sort
    this.csrStandards.update(standards => {
      const updated = standards.map(c => c.id === event.item.id ? updatedItem : c);
      return this.sortCsrStandards(updated);
    });

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.updateCsrStandard(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('ESG standard updated successfully');
        // 3. On success: Update with server response and re-sort to maintain alphabetical order
        this.csrStandards.update(standards => {
          const updated = standards.map(c => c.id === event.item.id ? response : c);
          return this.sortCsrStandards(updated);
        });
      },
      error: (error) => {
        console.error('Error updating ESG standard:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.csrStandards.update(standards =>
          standards.map(c => c.id === event.item.id ? originalItem : c)
        );
        this.toast.error(`<strong>Failed to update ESG Standard:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();
    const newStandard: CsrStandard = {
      id: tempId,
      name: event['name'],
      type: event['type'] ?? 1,
      systemReserved: false
    };

    // 1. Optimistically add to local state immediately and sort
    this.csrStandards.update(standards => this.sortCsrStandards([...standards, newStandard]));

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.createCsrStandard({
      name: event['name'],
      type: event['type'] ?? 1,
      systemReserved: false
    }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('ESG standard created successfully');
        // 3. On success: Replace temp item with server response and re-sort to maintain alphabetical order
        this.csrStandards.update(standards => {
          const updated = standards.map(c => c.id === tempId ? response : c);
          return this.sortCsrStandards(updated);
        });
      },
      error: (error) => {
        console.error('Error creating ESG standard:', error);
        this.spinnerService.hide();
        // 4. On error: Remove the temp item
        this.csrStandards.update(standards =>
          standards.filter(c => c.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create ESG Standard:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }
}
